import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, MapPin, Music, BookOpen, HandHeart, Coffee, Heart,
  Sparkles, Users, Church, CalendarDays, Send, Baby, Shirt, BadgeCheck, Video,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api } from "../lib/api";
import { IMG } from "../lib/images";
import { recapPdfUrl, recapDateFr } from "../lib/recaps";
import { useSettings } from "../lib/settings";
import { MaskedLines, Reveal, Marquee, SectionHeading } from "../components/motion";
import NextGathering from "../components/NextGathering";
import EventCard from "../components/EventCard";
import { FileText, Download } from "lucide-react";

/* ------------------------------ Hero ------------------------------ */
function Hero() {
  const { settings } = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-obsidian">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMG.hero} alt="Moment de communion fraternelle à La Maison de la Charité" className="h-full w-full object-cover" loading="eager" />
      </motion.div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(28,25,23,0.88),rgba(28,25,23,0.35)_55%,rgba(28,25,23,0.15))]" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-obsidian/80 to-transparent" />

      <div className="container-site relative flex min-h-[100svh] flex-col justify-end pb-10 pt-28">
        <div className="grid items-end gap-10 lg:grid-cols-[1.5fr_1fr]">
          <div className="pb-2">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.7 }}
              className="label-eyebrow !text-terracotta-soft"
            >
              Église évangélique · {settings.city_line}
            </motion.p>
            <MaskedLines
              lines={["Bienvenue à", "La Maison", "de la Charité"]}
              className="mt-5 font-serif text-[2.9rem] font-medium leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-7xl"
            />
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.7 }}
              className="mt-6 max-w-lg text-base leading-relaxed text-stone-200 sm:text-lg"
            >
              Une maison pour aimer Dieu, aimer les autres et grandir ensemble avec Jésus-Christ.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <Link data-testid="hero-cta-first-visit" to="/premiere-visite" className="btn-primary">
                Je viens pour la première fois
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link data-testid="hero-cta-discover" to="/qui-sommes-nous" className="btn-ghost-light">
                Découvrir La Maison de la Charité
              </Link>
              <Link
                data-testid="hero-cta-prayer"
                to="/priere"
                className="px-2 py-3 text-sm font-semibold text-stone-200 underline decoration-terracotta decoration-2 underline-offset-8 transition-colors hover:text-white"
              >
                Demander la prière
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] font-medium text-stone-300"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-terracotta-soft">{settings.meeting_name}</span>
              <span className="flex items-center gap-1.5"><Music className="h-3.5 w-3.5" /> Louange</span>
              <span className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Enseignement</span>
              <span className="flex items-center gap-1.5"><HandHeart className="h-3.5 w-3.5" /> Prière</span>
              <span className="flex items-center gap-1.5"><Coffee className="h-3.5 w-3.5" /> Communion fraternelle</span>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:pb-2"
          >
            <NextGathering dark />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Événement spécial ------------------------- */
function FeaturedEvents() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/events").then(({ data }) => setItems(data.filter((e) => e.featured))).catch(() => {});
  }, []);
  if (!items.length) return null;
  const ev = items[0];
  const dt = new Date(ev.date);
  return (
    <section data-testid="featured-event-section" className="py-14 sm:py-20">
      <div className="container-site">
        <Reveal>
          <div className="overflow-hidden rounded-[2rem] border border-terracotta/25 bg-gradient-to-br from-terracotta-soft via-white to-white shadow-[0_24px_60px_-30px_rgba(200,90,50,0.45)] lg:grid lg:grid-cols-[380px_1fr]">
            {ev.image && (
              <img src={ev.image} alt={ev.title} className="h-72 w-full object-cover object-top lg:h-full" loading="lazy" />
            )}
            <div className="p-8 sm:p-12">
              <p className="label-eyebrow">Événement spécial — à ne pas manquer</p>
              <h2 className="mt-3 font-serif text-3xl tracking-tight text-obsidian sm:text-4xl">{ev.title}</h2>
              <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-semibold text-terracotta">
                <span className="flex items-center gap-1.5 capitalize">
                  <CalendarDays className="h-4 w-4" />
                  {format(dt, "EEEE d MMMM yyyy 'dès' HH'h'mm", { locale: fr })}
                </span>
                {ev.location && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4" />
                    {ev.location}
                  </span>
                )}
              </p>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-stone-600 sm:text-base">{ev.description}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link data-testid="featured-event-visit" to="/premiere-visite" className="btn-primary">
                  Je viens
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {ev.location && (
                  <a
                    data-testid="featured-event-itinerary"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.location)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-outline"
                  >
                    <MapPin className="h-4 w-4" />
                    Itinéraire
                  </a>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------------------- Bienvenue ---------------------------- */
function Welcome() {
  return (
    <section data-testid="welcome-section" className="py-20 sm:py-28">
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            <img src={IMG.welcome} alt="Personnes souriantes qui s'accueillent" className="aspect-[4/3] w-full rounded-[2rem] object-cover" loading="lazy" />
            <div className="absolute -bottom-5 -right-3 rounded-2xl bg-terracotta px-5 py-4 text-white shadow-lg sm:-right-5">
              <p className="font-serif text-lg italic leading-snug">« Venez comme<br />vous êtes. »</p>
            </div>
          </div>
        </Reveal>
        <div>
          <SectionHeading eyebrow="Bienvenue à la maison" title="Vous êtes le bienvenu" />
          <Reveal delay={0.1}>
            <p className="mt-6 text-base leading-relaxed text-stone-600">
              Que vous soyez chrétien depuis longtemps, en recherche, rempli de questions ou simplement
              curieux de découvrir la foi chrétienne, vous êtes le bienvenu à La Maison de la Charité.
              Vous n'avez pas besoin de connaître les codes d'une église pour venir.
              Venez simplement comme vous êtes.
            </p>
            <p className="mt-4 text-base leading-relaxed text-stone-600">
              Vous n'êtes pas seulement invité à une réunion. Vous êtes invité à rencontrer une famille.
            </p>
            <Link data-testid="welcome-cta-prepare" to="/premiere-visite" className="btn-primary mt-8">
              Préparer ma première visite
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Charity Times --------------------------- */
const STEPS = [
  { icon: Music, img: IMG.louange, title: "Louange", text: "Un temps de chants et d'adoration pour tourner nos cœurs vers Dieu, ensemble." },
  { icon: BookOpen, img: IMG.enseignement, title: "Enseignement", text: "Un message biblique clair et concret, accessible même si vous découvrez la foi." },
  { icon: HandHeart, img: IMG.priere, title: "Prière", text: "Un espace pour prier, être écouté et confier ce qui compte pour vous." },
  { icon: Coffee, img: IMG.convivialite, title: "Convivialité fraternelle", text: "Café, échanges et vraies rencontres : on prend le temps de se connaître." },
];

function CharityTimes() {
  const { settings } = useSettings();
  return (
    <section data-testid="charity-times-section" className="bg-sand py-20 sm:py-28">
      <div className="container-site">
        <SectionHeading
          eyebrow="Notre rencontre principale · un samedi sur deux"
          title={settings.meeting_name}
          intro="Un samedi sur deux, de 16h à 18h : un temps pour louer Dieu, écouter un enseignement biblique, prier, partager et rencontrer d'autres personnes. Voici à quoi ressemble une rencontre, pas à pas."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <article data-testid={`charity-step-${i + 1}`} className="card-warm group h-full overflow-hidden">
                <div className="relative h-40 overflow-hidden">
                  <img src={s.img} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <span className="absolute left-4 top-4 rounded-full bg-cream/90 px-3 py-1 font-mono text-[11px] font-semibold tracking-widest text-terracotta backdrop-blur">
                    0{i + 1}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="flex items-center gap-2 font-serif text-lg font-semibold tracking-tight text-obsidian">
                    <s.icon className="h-4 w-4 text-terracotta" />
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone-600">{s.text}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15} className="mt-10">
          <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
            <div className="card-warm flex flex-col justify-center p-7 sm:p-9">
              <p className="label-eyebrow">Informations pratiques</p>
              <p className="mt-3 font-serif text-xl font-medium text-obsidian sm:text-2xl">
                Louange → Enseignement → Prière → Convivialité fraternelle
              </p>
              <p className="mt-3 text-sm leading-relaxed text-stone-600">
                Aucune tenue particulière n'est demandée, aucune inscription n'est nécessaire et rien
                ne vous sera jamais demandé. Il y a une place pour vous.
              </p>
            </div>
            <NextGathering />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------ Vision ------------------------------ */
const PILLARS = [
  { icon: Heart, title: "Aimer Dieu", text: "Développer une relation personnelle avec Jésus-Christ par la Parole, la prière et l'adoration." },
  { icon: Users, title: "Aimer les autres", text: "Construire une communauté marquée par la bienveillance, la compassion, le pardon et l'entraide." },
  { icon: Sparkles, title: "Grandir", text: "Aider chacun à devenir un disciple mature de Jésus-Christ et à découvrir ce que Dieu a placé dans sa vie." },
  { icon: HandHeart, title: "Servir", text: "Porter l'Évangile au-delà des murs de l'église et manifester concrètement l'amour de Dieu autour de nous." },
];

function Vision() {
  return (
    <section data-testid="vision-section" className="py-20 sm:py-28">
      <div className="container-site">
        <SectionHeading
          eyebrow="Notre vision"
          title="Une maison où l'amour de Dieu devient visible"
        />
        <Reveal delay={0.1} className="mt-8 max-w-2xl border-l-2 border-terracotta pl-6">
          <p className="verse-quote">
            « Bien-aimés, aimons-nous les uns les autres ; car l'amour est de Dieu. »
          </p>
          <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-stone-500">1 Jean 4:7</p>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08} className="h-full">
              <div data-testid={`vision-pillar-${i + 1}`} className="group flex h-full flex-col bg-white p-7 transition-colors duration-500 hover:bg-terracotta-soft/60 sm:p-8">
                <span className="font-serif text-4xl font-medium text-terracotta/30 transition-colors group-hover:text-terracotta">0{i + 1}</span>
                <h3 className="mt-5 font-serif text-xl font-semibold tracking-tight text-obsidian">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-600">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- Événements ---------------------------- */
function EventsPreview() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data.slice(0, 3))).catch(() => {});
  }, []);
  if (!events.length) return null;
  return (
    <section data-testid="events-section" className="bg-sand py-20 sm:py-28">
      <div className="container-site">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="Agenda" title="Prochains événements" />
          <Reveal delay={0.1}>
            <Link data-testid="events-see-all" to="/agenda" className="btn-outline">
              <CalendarDays className="h-4 w-4" />
              Tout l'agenda
            </Link>
          </Reveal>
        </div>
        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {events.map((ev, i) => (
            <Reveal key={ev.id} delay={i * 0.08}>
              <EventCard ev={ev} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Prière ------------------------------ */
function PrayerBand() {
  const { settings } = useSettings();
  const zoom = (settings.recurring || []).find((r) => r.link)?.link;
  return (
    <section data-testid="prayer-section" className="relative overflow-hidden bg-brown py-20 sm:py-28">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-terracotta/20 blur-3xl" />
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="label-eyebrow !text-terracotta-soft">Une maison de prière</p>
            <h2 className="mt-3 font-serif text-3xl leading-[1.12] tracking-tight text-cream sm:text-4xl">
              Nous pouvons prier pour vous
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-stone-300">
              Vous traversez une situation difficile ? Vous avez besoin de direction, de guérison,
              de paix, ou souhaitez simplement confier une situation à Dieu ? Vous pouvez nous
              transmettre votre sujet de prière, en toute confidentialité.
            </p>
            <p className="verse-quote mt-6 !text-stone-200">
              « Ne vous inquiétez de rien ; mais en toute chose faites connaître vos besoins à Dieu. »
            </p>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-stone-400">Philippiens 4:6</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link data-testid="prayer-cta" to="/priere" className="btn-primary">
                <Heart className="h-4 w-4" />
                Déposer un sujet de prière
              </Link>
              {zoom && (
                <a data-testid="prayer-zoom-cta" href={zoom} target="_blank" rel="noreferrer" className="btn-ghost-light">
                  <Video className="h-4 w-4" />
                  Prière en ligne ce mercredi · 19h (Zoom)
                </a>
              )}
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <img src={IMG.priere} alt="Famille réunie dans la prière" className="aspect-[4/3] w-full rounded-[2rem] object-cover" loading="lazy" />
        </Reveal>
      </div>
    </section>
  );
}

/* --------------------------- Dernier message --------------------------- */
function LatestSermon() {
  const [sermon, setSermon] = useState(undefined);
  useEffect(() => {
    api.get("/sermons").then(({ data }) => setSermon(data[0] || null)).catch(() => setSermon(null));
  }, []);
  return (
    <section data-testid="sermon-section" className="py-20 sm:py-28">
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem]">
            <img src={sermon?.image || IMG.books} alt="Enseignement biblique" className="aspect-[4/3] w-full object-cover" loading="lazy" />
            {sermon && (
              <span className="absolute left-5 top-5 rounded-full bg-cream/90 px-4 py-1.5 text-xs font-semibold text-terracotta backdrop-blur">
                Dernier message
              </span>
            )}
          </div>
        </Reveal>
        <div>
          <SectionHeading eyebrow="Prédications & ressources" title={sermon ? sermon.title : "Nourrir votre foi, semaine après semaine"} />
          <Reveal delay={0.1}>
            {sermon ? (
              <div className="mt-5 space-y-2 text-sm text-stone-600">
                {sermon.verse && <p className="verse-quote !text-base">« {sermon.verse} »</p>}
                {sermon.summary && <p className="leading-relaxed">{sermon.summary}</p>}
              </div>
            ) : (
              <p className="mt-5 text-base leading-relaxed text-stone-600">
                Retrouvez bientôt ici nos messages, enseignements et résumés : vidéos, audio et
                documents pour grandir dans la foi, à votre rythme.
              </p>
            )}
            <Link data-testid="sermon-cta-library" to="/predications" className="btn-outline mt-8">
              <BookOpen className="h-4 w-4" />
              Parcourir les messages
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Recap' ------------------------------ */
function RecapSection() {
  const [recap, setRecap] = useState(null);
  useEffect(() => {
    api.get("/recaps").then(({ data }) => setRecap(data[0] || null)).catch(() => {});
  }, []);
  if (!recap) return null;
  return (
    <section data-testid="recap-section" className="bg-sand py-20 sm:py-28">
      <div className="container-site">
        <Reveal>
          <div className="card-warm flex flex-col items-start gap-8 p-8 sm:p-12 lg:flex-row lg:items-center">
            <span className="inline-flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-terracotta text-white">
              <FileText className="h-7 w-7" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="label-eyebrow">Le Recap' de samedi dernier</p>
              <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-obsidian sm:text-3xl">{recap.title}</h2>
              <p className="mt-2 text-sm capitalize text-stone-500">{recapDateFr(recap.date)}</p>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-stone-600">
                Le résumé de la prédication en PDF : à relire tranquillement, et à partager avec quelqu'un que cela pourrait encourager.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a data-testid="recap-home-download" href={recapPdfUrl(recap)} download className="btn-primary">
                <Download className="h-4 w-4" />
                Télécharger le Recap'
              </a>
              <Link data-testid="recap-home-all" to="/recaps" className="btn-outline">
                Tous les Recap'
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------ Galerie ------------------------------ */
function Gallery() {
  return (
    <section data-testid="gallery-section" className="bg-sand py-20 sm:py-28">
      <div className="container-site">
        <SectionHeading eyebrow="Notre communauté" title="Découvrir notre communauté en images" />
        <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
          {IMG.gallery.map((src, i) => (
            <Reveal key={src} delay={(i % 4) * 0.07} className={i % 3 === 0 ? "row-span-2" : ""}>
              <img
                src={src}
                alt="Vie de la communauté"
                loading="lazy"
                className={`w-full rounded-2xl object-cover transition-transform duration-700 hover:scale-[1.03] ${i % 3 === 0 ? "aspect-[3/4] md:h-full" : "aspect-square"}`}
              />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Première visite --------------------------- */
function FirstVisitTeaser() {
  const points = [
    { icon: Shirt, text: "Aucune tenue particulière n'est demandée" },
    { icon: Baby, text: "Vos enfants sont les bienvenus" },
    { icon: BadgeCheck, text: "Aucune inscription, aucune obligation de donner" },
  ];
  return (
    <section data-testid="first-visit-section" className="py-20 sm:py-28">
      <div className="container-site">
        <div className="card-warm overflow-hidden lg:grid lg:grid-cols-2">
          <img src={IMG.firstVisit} alt="Accueil chaleureux lors d'une rencontre" className="h-64 w-full object-cover lg:h-full" loading="lazy" />
          <div className="p-8 sm:p-12">
            <p className="label-eyebrow">Première visite</p>
            <h2 className="mt-3 font-serif text-3xl leading-[1.12] tracking-tight text-obsidian sm:text-4xl">
              Votre première fois parmi nous ?
            </h2>
            <ul className="mt-7 space-y-4">
              {points.map((p) => (
                <li key={p.text} className="flex items-start gap-3 text-sm font-medium text-stone-700 sm:text-base">
                  <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-terracotta-soft text-terracotta">
                    <p.icon className="h-4 w-4" />
                  </span>
                  {p.text}
                </li>
              ))}
            </ul>
            <Link data-testid="first-visit-cta" to="/premiere-visite" className="btn-primary mt-9">
              Tout savoir sur ma première visite
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- Témoignages ---------------------------- */
function TestimonialsPreview() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setItems(data.slice(0, 3))).catch(() => {});
  }, []);
  return (
    <section data-testid="testimonials-section" className="bg-sand py-20 sm:py-28">
      <div className="container-site">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="Témoignages" title="Des vies touchées, des histoires vraies" />
          <Reveal delay={0.1}>
            <Link data-testid="testimonials-see-all" to="/temoignages" className="btn-outline">Tous les témoignages</Link>
          </Reveal>
        </div>
        {items.length ? (
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {items.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}>
                <blockquote className="card-warm h-full p-7">
                  <p className="text-sm leading-relaxed text-stone-600">« {t.text} »</p>
                  <footer className="mt-5 font-serif text-base font-semibold text-obsidian">{t.name}</footer>
                </blockquote>
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal className="mt-10">
            <div className="card-warm border-dashed p-10 text-center">
              <Church className="mx-auto h-6 w-6 text-terracotta" />
              <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-stone-500">
                Les premiers témoignages de personnes de l'église seront publiés ici prochainement —
                des histoires authentiques, jamais inventées.
              </p>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

/* ----------------------------- Newsletter ----------------------------- */
function Newsletter() {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email, website });
      toast.success("Merci ! Vous serez informé des prochaines rencontres.");
      setEmail("");
    } catch {
      toast.error("Impossible d'enregistrer votre email. Réessayez dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="newsletter-section" className="py-20 sm:py-28">
      <div className="container-site">
        <div className="rounded-[2rem] bg-terracotta px-7 py-14 text-center text-white sm:px-12">
          <Reveal>
            <p className="label-eyebrow !text-white/80">Rester informé</p>
            <h2 className="mx-auto mt-3 max-w-xl font-serif text-3xl leading-[1.12] tracking-tight sm:text-4xl">
              Recevez les prochaines dates et les nouvelles de la maison
            </h2>
            <form data-testid="newsletter-form" onSubmit={submit} className="mx-auto mt-8 flex max-w-md flex-col gap-3 sm:flex-row">
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre adresse email"
                className="w-full flex-1 rounded-full border border-white/30 bg-white/10 px-5 py-3.5 text-sm text-white placeholder:text-white/60 focus:border-white focus:outline-none"
              />
              <button data-testid="newsletter-submit-btn" type="submit" disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-terracotta transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                <Send className="h-4 w-4" />
                {loading ? "Envoi…" : "S'inscrire"}
              </button>
            </form>
            <p className="mt-4 text-xs text-white/70">Respect de votre vie privée — désinscription à tout moment.</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- Localisation ----------------------------- */
function Location() {
  const { settings } = useSettings();
  return (
    <section data-testid="location-section" className="pb-20 sm:pb-28">
      <div className="container-site grid gap-8 lg:grid-cols-[1fr_1.3fr]">
        <Reveal>
          <div className="card-warm h-full p-8 sm:p-10">
            <p className="label-eyebrow">Nous trouver</p>
            <h2 className="mt-3 font-serif text-3xl tracking-tight text-obsidian">Montévrain · Val d'Europe</h2>
            <p className="mt-4 flex items-start gap-2.5 text-sm leading-relaxed text-stone-600">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
              {settings.address}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-stone-600">
              À quelques minutes de Serris, Chessy et Disneyland Paris — facile d'accès en voiture
              comme en transports (RER A, gare Val d'Europe).
            </p>
            <a data-testid="location-itinerary-btn" href={settings.maps_url} target="_blank" rel="noreferrer" className="btn-primary mt-8">
              <MapPin className="h-4 w-4" />
              Ouvrir dans Google Maps
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="h-full min-h-[320px] overflow-hidden rounded-[2rem] border border-line">
            <iframe
              data-testid="location-map"
              title="Carte — La Maison de la Charité, Montévrain"
              src="https://www.google.com/maps?q=R%C3%A9sidence%20du%20Parc%2C%202%20Rue%20Edouard%20Buffard%2C%2077144%20Mont%C3%A9vrain&z=15&output=embed"
              className="map-frame h-full min-h-[320px] w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------- Page -------------------------------- */
export default function Home() {
  return (
    <>
      <Hero />
      <Marquee
        items={[
          "Bienvenue à la maison",
          "Venez comme vous êtes",
          "Il y a une place pour vous",
          "Découvrir Jésus · Grandir ensemble · Aimer concrètement",
          "Une maison pour aimer Dieu et aimer les autres",
        ]}
      />
      <FeaturedEvents />
      <Welcome />
      <CharityTimes />
      <Vision />
      <EventsPreview />
      <PrayerBand />
      <LatestSermon />
      <RecapSection />
      <Gallery />
      <FirstVisitTeaser />
      <TestimonialsPreview />
      <Newsletter />
      <Location />
    </>
  );
}
